/*
 * CoinReveal — reusable coin-flip + count-up ceremony.
 *
 * Renders a spinning coin, counts up each claim in the list, then decelerates
 * on a random face while counting the total. Optionally taps to replay.
 *
 * Direct port of the original DC-runtime Logic class's runCoinSequence(),
 * generalised to accept N claims via props. Audio via WebAudio (opt-outable).
 *
 * Usage:
 *   <CoinReveal
 *     claims={[
 *       { label: "WELCOME BONUS", amount: 10000 },
 *       { label: "EMAIL COIN CLAIMS", amount: 2000 },
 *     ]}
 *     onClaim={() => navigate("/eroscoach/..." )}
 *     claimLabel="START YOUR 1st SESSION"
 *     claimSubtext="Your first session begins instantly"
 *     replayable={true}
 *     soundEnabled={true}
 *     totalLabel="TOTAL"
 *     autoStart={true}
 *   />
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./CoinReveal.css";

const COIN_ASSETS = {
  edge:  "/rewards/eros-coin-edge.png",
  front: "/rewards/eros-coin2.png",
  back:  "/rewards/eros-coin3.png",
};

const DEFAULT_PHASE_DUR_COUNT = 2000;
const DEFAULT_PHASE_DUR_HOLD  = 1500;
const BASE_SPIN_CYCLE_SEC     = 0.75;

function faceVisual(d) {
  // Same shape as the original: 20% window either side of the crossover.
  return d <= 0.20
    ? { scaleX: 1 - (d / 0.20) * 0.7, opacity: 1 }
    : { scaleX: 0.05, opacity: 0 };
}

function buildPhases(claims, totalLabel) {
  if (!claims || claims.length === 0) return [];
  const sum = claims.reduce((a, c) => a + (c.amount || 0), 0);

  if (claims.length === 1) {
    // Single claim: one count phase that IS the final (decelerates + lands).
    return [{
      type: "count",
      from: 0,
      to: claims[0].amount,
      dur: DEFAULT_PHASE_DUR_COUNT,
      label: claims[0].label,
      prefix: "+",
      isFinal: true,
    }];
  }

  const phases = [];
  claims.forEach((c, i) => {
    phases.push({
      type: "count",
      from: 0,
      to: c.amount,
      dur: DEFAULT_PHASE_DUR_COUNT,
      label: c.label,
      prefix: "+",
    });
    if (i < claims.length - 1) {
      phases.push({
        type: "hold",
        value: c.amount,
        dur: DEFAULT_PHASE_DUR_HOLD,
        label: c.label,
        prefix: "+",
      });
    }
  });
  // Brief hold on the last claim before sweeping to the grand total, so the
  // user can read the sub-total before it changes.
  phases.push({
    type: "hold",
    value: claims[claims.length - 1].amount,
    dur: DEFAULT_PHASE_DUR_HOLD,
    label: claims[claims.length - 1].label,
    prefix: "+",
  });
  // Total sweep from the last claim's amount up to the grand total.
  phases.push({
    type: "count",
    from: claims[claims.length - 1].amount,
    to: sum,
    dur: DEFAULT_PHASE_DUR_COUNT,
    label: totalLabel,
    prefix: "",
    isFinal: true,
  });
  return phases;
}

export const CoinReveal = ({
  claims,
  totalLabel = "TOTAL",
  claimLabel = "CLAIM",
  claimSubtext = "",
  subtitle = "Use them to unlock deeper coaching sessions & insights",
  onClaim,
  replayable = true,
  soundEnabled = true,
  autoStart = true,
  showBreakdown = true,
}) => {
  const [coinCount, setCoinCount] = useState(0);
  const [coinLabel, setCoinLabel] = useState(claims?.[0]?.label ?? "");
  const [coinPrefix, setCoinPrefix] = useState("+");
  const [face, setFace] = useState({
    frontScaleX: 1, frontOpacity: 1, backScaleX: 0.05, backOpacity: 0,
  });
  const [spinning, setSpinning] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const stateRef = useRef({
    spinTotal: 0,
    spinVelocity: 1 / BASE_SPIN_CYCLE_SEC,
    decelActive: false,
    decelStart: 0,
    decelEnd: 0,
    decelStartTime: 0,
    decelDur: DEFAULT_PHASE_DUR_COUNT,
    lastTick: 0,
  });

  const phases = useMemo(
    () => buildPhases(claims, totalLabel),
    [claims, totalLabel]
  );

  // --- audio helpers ------------------------------------------------------
  const ensureAudio = useCallback(() => {
    if (!soundEnabled) return null;
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtxRef.current = new Ctx();
      } catch { return null; }
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, [soundEnabled]);

  const playTick = useCallback((freq, dur, vol) => {
    const ctx = ensureAudio();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t); osc.stop(t + dur);
  }, [ensureAudio]);

  const playChime = useCallback(() => {
    playTick(1046, 0.16, 0.09);
    setTimeout(() => playTick(1568, 0.22, 0.08), 90);
  }, [playTick]);

  // --- run sequence -------------------------------------------------------
  const runSequence = useCallback(() => {
    if (!phases.length) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const st = stateRef.current;
    st.spinTotal = 0;
    st.decelActive = false;
    st.lastTick = 0;
    setSpinning(true);
    setCoinCount(0);
    setCoinLabel(phases[0].label);
    setCoinPrefix(phases[0].prefix);

    let phaseIdx = 0;
    let phaseStart = performance.now();
    let lastTime = phaseStart;

    const step = (now) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      const phase = phases[phaseIdx];
      const elapsed = now - phaseStart;

      // Advance the spin (or decelerate if this is the final phase).
      if (phase.isFinal) {
        if (!st.decelActive) {
          st.decelActive = true;
          const startTotal = st.spinTotal;
          const targetFrac = Math.random() < 0.5 ? 0 : 0.5; // land front or back
          const extraSpins = 3 + Math.floor(Math.random() * 3);
          const frac0 = startTotal - Math.floor(startTotal);
          const deltaFracNeeded = (targetFrac - frac0 + 1) % 1;
          st.decelStart = startTotal;
          st.decelEnd = startTotal + extraSpins + deltaFracNeeded;
          st.decelStartTime = now;
          st.decelDur = phase.dur;
        }
        const dt2 = Math.min(1, (now - st.decelStartTime) / st.decelDur);
        const eased = 1 - Math.pow(1 - dt2, 3); // easeOutCubic
        st.spinTotal = st.decelStart + (st.decelEnd - st.decelStart) * eased;
      } else {
        st.spinTotal += st.spinVelocity * dt;
      }

      const frac = st.spinTotal - Math.floor(st.spinTotal);
      const fv = faceVisual(Math.min(frac, 1 - frac));
      const bv = faceVisual(Math.abs(frac - 0.5));
      setFace({
        frontScaleX: fv.scaleX, frontOpacity: fv.opacity,
        backScaleX:  bv.scaleX, backOpacity:  bv.opacity,
      });

      if (phase.type === "count") {
        const t = Math.min(1, elapsed / phase.dur);
        const eased = 1 - Math.pow(1 - t, 3);
        const val = Math.round(phase.from + (phase.to - phase.from) * eased);
        setCoinCount(val);
        setCoinLabel(phase.label);
        setCoinPrefix(phase.prefix);
        if (now - st.lastTick > 55 && t < 1) {
          st.lastTick = now;
          playTick(560 + eased * 500, 0.045, 0.045);
        }
        if (t >= 1) {
          phaseIdx++;
          phaseStart = now;
          playChime();
        }
      } else {
        setCoinCount(phase.value);
        setCoinLabel(phase.label);
        setCoinPrefix(phase.prefix);
        if (elapsed >= phase.dur) {
          phaseIdx++;
          phaseStart = now;
        }
      }

      if (phaseIdx >= phases.length) {
        rafRef.current = null;
        setSpinning(false);
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, [phases, playTick, playChime]);

  // --- lifecycle ----------------------------------------------------------
  useEffect(() => {
    if (autoStart) runSequence();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- interactions -------------------------------------------------------
  const onCoinTap = () => {
    if (!replayable || spinning || claimed) return;
    runSequence();
  };
  const onClaimClick = () => {
    if (claimed || spinning) return;
    setClaimed(true);
    if (typeof onClaim === "function") setTimeout(() => onClaim(), 900);
  };

  // --- burst dots (memoised; 10 evenly-angled) ----------------------------
  const burstDots = useMemo(
    () => Array.from({ length: 10 }, (_, i) => ({
      angle: i * 36,
      dur: 1.4 + (i % 3) * 0.3,
      delay: (i * 0.08).toFixed(2),
    })),
    []
  );

  const formattedCount = (coinCount ?? 0).toLocaleString("en-US");
  const total = useMemo(
    () => (claims || []).reduce((a, c) => a + (c.amount || 0), 0),
    [claims]
  );

  return (
    <div className="cr-root" data-testid="coin-reveal">
      <div className="cr-coin-wrap">
        <div className="cr-glow" aria-hidden />
        {burstDots.map((b, i) => (
          <div
            key={i}
            className="cr-burst-dot"
            style={{
              "--cr-angle": `${b.angle}deg`,
              animationDuration: `${b.dur}s`,
              animationDelay: `${b.delay}s`,
            }}
            aria-hidden
          />
        ))}
        <div
          className={`cr-coin ${replayable ? "cr-replayable" : "cr-not-replayable"}`}
          onClick={onCoinTap}
          role={replayable ? "button" : undefined}
          tabIndex={replayable && !spinning ? 0 : -1}
          onKeyDown={(e) => {
            if (replayable && !spinning && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              runSequence();
            }
          }}
          aria-label={replayable ? "Tap to replay coin reveal" : "Reward coin"}
          data-testid="coin-reveal-coin"
        >
          <img className="cr-coin-edge" src={COIN_ASSETS.edge} alt="" aria-hidden />
          <div className="cr-coin-faces">
            <img
              className="cr-coin-face"
              src={COIN_ASSETS.front}
              alt="Eros coin front"
              style={{ transform: `scaleX(${face.frontScaleX})`, opacity: face.frontOpacity }}
            />
            <img
              className="cr-coin-face"
              src={COIN_ASSETS.back}
              alt="Eros coin back"
              style={{ transform: `scaleX(${face.backScaleX})`, opacity: face.backOpacity }}
            />
          </div>
        </div>
      </div>

      <div className="cr-count-block">
        <div className="cr-count" data-testid="coin-reveal-count">
          {coinPrefix}{formattedCount}
        </div>
        <div className="cr-label" data-testid="coin-reveal-label">{coinLabel}</div>
      </div>

      {showBreakdown && claims && claims.length > 1 && !spinning && (
        <div className="cr-breakdown" data-testid="coin-reveal-breakdown">
          {claims.map((c, i) => (
            <div key={i} className="cr-breakdown-row">
              <span>{c.label}</span>
              <span className="cr-breakdown-amt">{c.amount >= 0 ? `+${c.amount.toLocaleString("en-US")}` : c.amount.toLocaleString("en-US")}</span>
            </div>
          ))}
          <div className="cr-breakdown-row cr-row-total">
            <span>{totalLabel}</span>
            <span className="cr-breakdown-amt">{total >= 0 ? `+${total.toLocaleString("en-US")}` : total.toLocaleString("en-US")}</span>
          </div>
        </div>
      )}

      {subtitle ? (
        <div className="cr-sub">{subtitle}</div>
      ) : null}

      <button
        className="cr-claim"
        onClick={onClaimClick}
        disabled={claimed || spinning}
        data-testid="coin-reveal-claim-btn"
      >
        {claimed ? "ENTERING SESSION…" : claimLabel}
      </button>
      <div className="cr-claim-sub" data-testid="coin-reveal-claim-sub">
        {claimed ? "Preparing your first session with Agent Eros" : claimSubtext}
      </div>
    </div>
  );
};

export default CoinReveal;
